'use client'

import styles from '@/app/_components/EventDetails/EventDetails.module.css'
import StringToReactNode from '@/app/_components/StringToReactNode/StringToReactNode'
import { ReactNode, useMemo } from 'react'
import {
  checkIsRepeatingEvent,
  getEventMeta,
  getFormattedEventTimeSlots,
  handleVenueMeta,
  hasMeta,
} from '@/utils/events.utils'
import { useEvent, useEventVenue } from '@/app/_providers/EventProvider/EventProvider'
import { useSeller } from '@/app/_providers/SellerProvider/SellerProvider'
import { PORTAL_SELECTORS } from '@/config/navigation.config'
import { AnchorButton } from '@/app/_components/AnchorButton'
import VenueAddress from '@/app/_components/EventDetails/VenueAddress'
import SeatedNotice from '@/app/_components/EventDetails/SeatedNotice'
import TXT from '@/messages'

const LocationCard = (): ReactNode => {
  const eventVenue = useEventVenue()
  const { timezone, eventDetailsAlert, faqUrl } = useSeller()
  const primaryEventData = useEvent<Transformed.EventWithTimeslotsAndMeta>()

  const isRecurringEvent = useMemo(() => checkIsRepeatingEvent(primaryEventData), [primaryEventData])
  const meta = getEventMeta(primaryEventData.meta)
  const { venueTitle } = handleVenueMeta(meta, eventVenue)
  const { formattedDate, formattedTime } = getFormattedEventTimeSlots(primaryEventData.timeslots, timezone, meta, 'details')
  const exhibitionHours = hasMeta('exhibitionHours', meta) && meta.exhibitionHours

  return (
    <section className={styles.EventLocationCard}>
      <img
        className={styles.IconContainer}
        src="/static/events/svg/location-outlined.svg"
        alt={TXT.eventsA11y.locationPin}
      />
      <address>
        <h2 className={styles.EventLocationHeader}>
          {venueTitle}
        </h2>
        <VenueAddress />
      </address>

      <div className={styles.IconContainer}>
        <img
          src="/static/events/svg/info-outlined.svg"
          alt={TXT.eventsA11y.informationIcon}
        />
        <div id={PORTAL_SELECTORS.lifeTimePass} />
      </div>
      <div>
        <h2 className={styles.EventLocationSubhead}>
          {TXT.eventsPage.ages}
          {' '}
          {hasMeta('eventAge', meta) ? meta.eventAge : TXT.events.eventAllAges}
        </h2>
        {!isRecurringEvent && (formattedTime || formattedDate) && <div>{formattedTime || formattedDate}</div>}
        {exhibitionHours && <div>{exhibitionHours}</div>}
        {hasMeta('eventNote', meta) && (
          <StringToReactNode incomingString={meta.eventNote} />
        )}
      </div>

      <SeatedNotice />

      <img
        className={styles.IconContainer}
        src="/static/events/svg/question-mark-outlined.svg"
        alt={TXT.eventsA11y.questionMarkIcon}
      />
      <h2 className={styles.EventLocationSubhead}>
        {TXT.eventsPage.faq}
        <AnchorButton
          suppressHydrationWarning
          className={styles.FAQButton}
          href={faqUrl}
          target="_blank"
          testId="faq-link"
          rel="noreferrer noopener"
          variant="outline"
          size="xs"
          weight="bold"
          uppercase
        >
          {TXT.eventsPage.clickHere}
        </AnchorButton>
      </h2>

      {eventDetailsAlert && (
        <>
          <div className={styles.IconContainer} data-testid="exclamation-icon">
            <img
              src="/static/events/svg/exclamation.svg"
              alt={TXT.eventsA11y.exclamationIcon}
            />
          </div>
          <div className={styles.EventDetailsSellerAlertContainer} data-testid="no-resellers-disclaimer">
            <StringToReactNode incomingString={eventDetailsAlert} />
          </div>
        </>
      )}
    </section>
  )
}

export default LocationCard
